import { useTranslation } from '@rhino-project/core/i18n';
import { useState } from 'react';
import { notFound, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { AlertDialog, Button, Spinner } from '@heroui/react';

import {
  useBaseOwnerId,
  useHasRoleOf,
  useModel,
  useModelDelete,
  useModelShow
} from '@rhino-project/core/hooks';
import { networkApiCall } from '@rhino-project/core/lib';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import Lineup from '../../components/lineup/Lineup';
import InlineEditLineupHeader from '../../components/lineup/InlineEditLineupHeader';

const LineupPage = () => {
  const { t } = useTranslation();

  const { id: lineupId } = useParams({ strict: false });
  const navigate = useNavigate();
  const baseOwnerId = useBaseOwnerId();
  const isAdmin = useHasRoleOf('admin');
  const lineupModel = useModel('lineup');

  const {
    isLoading,
    resource: lineup,
    refetch
  } = useModelShow(lineupModel, lineupId!);

  const { mutate: duplicateLineup, isPending: isDuplicating } = useMutation({
    mutationFn: (sourceId: string | number) =>
      networkApiCall(`${lineupModel.path}/${sourceId}/duplicate`, {
        method: 'post'
      })
  });

  const { mutate: deleteLineup, isPending: isDeleting } =
    useModelDelete(lineupModel);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (isLoading) {
    return <Spinner size="sm" className="mx-auto mt-8 block" />;
  }

  if (!lineup) throw notFound();

  const lineupRecord = lineup;
  const teamSeasonId = lineupRecord.team_season?.id;

  const handleDuplicate = () => {
    duplicateLineup(lineupRecord.id, {
      onSuccess: (response) => {
        const data = (response as { data?: { id?: string | number } })?.data;
        const newId = data?.id;
        if (!newId) return;
        navigate({
          to: `/${baseOwnerId}/active/${teamSeasonId}/lineups/${newId}`
        });
      }
    });
  };

  const handleDelete = () => {
    deleteLineup(lineupRecord.id, {
      onSuccess: () => {
        setIsDeleteOpen(false);
        navigate({
          to: `/${baseOwnerId}/active/${teamSeasonId}/lineups`
        });
      }
    });
  };

  const lineupLabel = lineupRecord.name ?? t('frontend.messages.this_lineup');
  const isDepthChart = lineupRecord.kind === 'depth_chart';

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div />
          <InlineEditLineupHeader
            lineup={
              lineupRecord as {
                id: number | string;
                name?: string | null;
                event_at?: string | null;
                kind?: string | null;
              }
            }
            isAdmin={isAdmin}
            onSaved={refetch}
          />
          <div className="justify-self-end flex items-center gap-2">
            {!isDepthChart && (
              <Button
                size="sm"
                variant="secondary"
                onPress={handleDuplicate}
                isDisabled={isDuplicating}
              >
                {t('frontend.messages.duplicate')}
              </Button>
            )}
            {isAdmin && !isDepthChart && (
              <AlertDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <Button size="sm" variant="danger-soft" isDisabled={isDeleting}>
                  {t('frontend.messages.delete')}
                </Button>
                <AlertDialog.Backdrop>
                  <AlertDialog.Container size="sm">
                    <AlertDialog.Dialog>
                      <AlertDialog.CloseTrigger />
                      <AlertDialog.Header>
                        <AlertDialog.Icon status="danger" />
                        <AlertDialog.Heading>
                          {t('frontend.messages.delete_lineup')}
                        </AlertDialog.Heading>
                      </AlertDialog.Header>
                      <AlertDialog.Body>
                        {t('frontend.messages.delete_confirmation', {
                          name: lineupLabel
                        })}
                      </AlertDialog.Body>
                      <AlertDialog.Footer>
                        <Button
                          variant="ghost"
                          onPress={() => setIsDeleteOpen(false)}
                        >
                          {t('frontend.messages.cancel')}
                        </Button>
                        <Button
                          variant="danger"
                          onPress={handleDelete}
                          isDisabled={isDeleting}
                        >
                          {isDeleting
                            ? t('frontend.messages.deleting')
                            : t('frontend.messages.delete_lineup')}
                        </Button>
                      </AlertDialog.Footer>
                    </AlertDialog.Dialog>
                  </AlertDialog.Container>
                </AlertDialog.Backdrop>
              </AlertDialog>
            )}
          </div>
        </div>
        <Lineup lineup={lineupRecord} />
      </MaxWidth>
    </BaseAuthedPage>
  );
};

export default LineupPage;
